import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { ALL_LOCALES, DEFAULT_LOCALE, directionForLocale, type Locale } from '@lims/shared-types';
import ar from './locales/ar.json';
import en from './locales/en.json';

/**
 * Internationalisation setup.
 *
 * Rules:
 *  - Visible text is NEVER hardcoded in components. Everything routes through
 *    a translation key, enforced by lint in Phase 2.
 *  - Adding a language means adding a catalogue here, not rewriting interfaces.
 *  - The API returns stable error CODES; the message shown to the user comes
 *    from the `errors` namespace in these catalogues, so the API stays
 *    locale-independent and reusable by a future mobile client.
 */

export const resources = {
  ar: { translation: ar },
  en: { translation: en },
} as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...ALL_LOCALES],
    // Treat "ar-SA" as "ar" rather than falling back to the default language.
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    interpolation: {
      // React already escapes rendered values.
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'lims.language',
    },
    returnNull: false,
  });

/**
 * Apply the document direction and language for a locale.
 *
 * Arabic renders right-to-left and English left-to-right. Setting `dir` on the
 * document root lets CSS logical properties handle layout mirroring, rather
 * than each component branching on language.
 */
export function applyDocumentDirection(locale: Locale): void {
  if (typeof document === 'undefined') return;

  const direction = directionForLocale(locale);
  document.documentElement.setAttribute('dir', direction);
  document.documentElement.setAttribute('lang', locale);

  // Arabic uses a typeface with correct shaping and ligatures.
  document.documentElement.classList.toggle('font-arabic', locale === 'ar');
}

/** Change the interface language and update the document direction together. */
export async function changeLanguage(locale: Locale): Promise<void> {
  await i18n.changeLanguage(locale);
  applyDocumentDirection(locale);
}

/** The current locale, always one of the supported values. */
export function currentLocale(): Locale {
  const language = i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LOCALE;
  const base = language.split('-')[0] ?? DEFAULT_LOCALE;
  return (ALL_LOCALES as readonly string[]).includes(base)
    ? (base as Locale)
    : DEFAULT_LOCALE;
}

// Apply direction on first load so the very first paint is correct.
applyDocumentDirection(currentLocale());

export default i18n;
